import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, X, ExternalLink, Tag, Star,
  Archive, RefreshCw, Globe, BookOpen, CheckCircle2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { trendingService } from '../api/trendingApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// ── Helpers ───────────────────────────────────────────────────────────
const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const RelevanceDots = ({ score = 0 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <span key={i} className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: i <= Math.round(score * 5) ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft }} />
    ))}
  </div>
);

// ── Capture / Edit Form Modal ─────────────────────────────────────────
const ItemFormModal = ({ isOpen, onClose, onSave, initial = null }) => {
  const empty = {
    title: '', summary: '', url: '', source: '', region: '',
    category: '', tags: [], imageUrl: '', relevanceScore: 0,
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(initial ? { ...empty, ...initial } : empty);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, isOpen]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg rounded-2xl border p-6 max-h-[90vh] overflow-y-auto"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
            {initial ? 'Edit Inspiration' : 'Capture Inspiration'}
          </h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
            <input required type="text" value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="What's this about?"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Summary</label>
            <textarea value={form.summary} onChange={e => set('summary', e.target.value)}
              rows={3} placeholder="Brief description or key insight…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
            <input type="url" value={form.url} onChange={e => set('url', e.target.value)}
              placeholder="https://…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
              <input type="text" value={form.source} onChange={e => set('source', e.target.value)}
                placeholder="TechCrunch, Twitter…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Region</label>
              <input type="text" value={form.region} onChange={e => set('region', e.target.value)}
                placeholder="world, tz, africa…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <input type="text" value={form.category} onChange={e => set('category', e.target.value)}
                placeholder="AI, EdTech, Health…"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Relevance (0–1)</label>
              <input type="number" min={0} max={1} step={0.1}
                value={form.relevanceScore}
                onChange={e => set('relevanceScore', parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
            <input type="text" value={(form.tags || []).join(', ')}
              onChange={e => set('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="Comma-separated tags…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Image URL</label>
            <input type="url" value={form.imageUrl} onChange={e => set('imageUrl', e.target.value)}
              placeholder="https://…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>

          {initial && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.archived}
                onChange={e => set('archived', e.target.checked)} />
              <span style={{ color: SYSTEM_COLORS.textMuted }}>Archived</span>
            </label>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Saving…' : initial ? 'Update' : 'Capture'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── "Mark as used" Modal ──────────────────────────────────────────────
const UsedInModal = ({ isOpen, onClose, onSave, itemTitle }) => {
  const [contentItemId, setContentItemId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (isOpen) setContentItemId(''); }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contentItemId.trim()) return;
    setSaving(true);
    try {
      await onSave(contentItemId.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-sm rounded-2xl border p-6"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
            Mark as Used
          </h3>
          <button onClick={onClose}><X size={16} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: SYSTEM_COLORS.textMuted }}>
          Link content created from: <strong>{itemTitle}</strong>
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Content Item ID *</label>
            <input required type="text" value={contentItemId}
              onChange={e => setContentItemId(e.target.value)}
              placeholder="Content item ID…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }} />
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose}
              className="rounded-lg px-3 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Linking…' : 'Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Inspiration Card ──────────────────────────────────────────────────
const InspirationCard = ({ item, onEdit, onDelete, onArchive, onMarkUsed }) => (
  <div className={`rounded-xl border overflow-hidden flex flex-col transition-opacity ${item.archived ? 'opacity-60' : ''}`}
       style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
    {/* Thumbnail */}
    {item.imageUrl && (
      <div className="h-36 overflow-hidden bg-gray-100">
        <img src={item.imageUrl} alt={item.title}
             className="w-full h-full object-cover"
             onError={e => { e.target.style.display = 'none'; }} />
      </div>
    )}

    <div className="flex-1 p-4 space-y-2">
      {/* Category + region row */}
      <div className="flex items-center gap-2 flex-wrap">
        {item.category && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
            <BookOpen size={10} /> {item.category}
          </span>
        )}
        {item.region && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
            <Globe size={10} /> {item.region}
          </span>
        )}
        {item.archived && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
            <Archive size={10} /> Archived
          </span>
        )}
      </div>

      {/* Title */}
      <p className="font-semibold text-sm leading-snug" style={{ color: NEUTRAL_COLORS.black }}>
        {item.title}
      </p>

      {/* Summary */}
      {item.summary && (
        <p className="text-xs line-clamp-3" style={{ color: SYSTEM_COLORS.textMuted }}>
          {item.summary}
        </p>
      )}

      {/* Source + relevance */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {item.source && (
            <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>{item.source}</span>
          )}
          <span className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>
            {formatDate(item.capturedAt)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Star size={10} style={{ color: BRAND_COLORS.primary }} />
          <RelevanceDots score={item.relevanceScore} />
        </div>
      </div>

      {/* Tags */}
      {(item.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {item.tags.map(t => (
            <span key={t} className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs"
                  style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              <Tag size={9} /> {t}
            </span>
          ))}
        </div>
      )}

      {/* Used-in counter */}
      {(item.usedInContentIds || []).length > 0 && (
        <div className="flex items-center gap-1 text-xs text-green-600">
          <CheckCircle2 size={12} />
          Used in {item.usedInContentIds.length} content item{item.usedInContentIds.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>

    {/* Footer actions */}
    <div className="flex items-center justify-between px-4 py-2.5 border-t"
         style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
      <div className="flex items-center gap-1">
        {item.url && (
          <a href={item.url} target="_blank" rel="noopener noreferrer"
             className="p-1 text-gray-400 hover:text-blue-600 rounded" title="Open source">
            <ExternalLink size={14} />
          </a>
        )}
        <button onClick={() => onMarkUsed(item)} title="Mark as used in content"
          className="p-1 text-gray-400 hover:text-green-600 rounded">
          <CheckCircle2 size={14} />
        </button>
        {!item.archived && (
          <button onClick={() => onArchive(item)} title="Archive"
            className="p-1 text-gray-400 hover:text-yellow-600 rounded">
            <Archive size={14} />
          </button>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onEdit(item)} title="Edit"
          className="p-1 text-gray-400 hover:text-blue-600 rounded">
          <Edit2 size={14} />
        </button>
        <button onClick={() => onDelete(item)} title="Delete"
          className="p-1 text-gray-400 hover:text-red-600 rounded">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────
const REGIONS = ['world', 'africa', 'tz', 'ea', 'us', 'eu', 'asia'];

const TrendingFeed = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [limit, setLimit] = useState(100);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [usedInTarget, setUsedInTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await trendingService.list({ limit, region: region || undefined });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load trending items');
    } finally {
      setLoading(false);
    }
  }, [limit, region]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editingItem) {
        const updated = await trendingService.update(editingItem.id, form);
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        toast.success('Updated');
      } else {
        const created = await trendingService.capture(form);
        setItems(prev => [created, ...prev]);
        toast.success('Inspiration captured');
      }
      setEditingItem(null);
    } catch (err) {
      toast.error(err?.message || 'Save failed');
      throw err;
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await trendingService.delete(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleArchive = async (item) => {
    try {
      const updated = await trendingService.archive(item.id);
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      toast.success('Archived');
    } catch {
      toast.error('Failed to archive');
    }
  };

  const handleMarkUsed = async (contentItemId) => {
    try {
      const updated = await trendingService.recordUsedIn(usedInTarget.id, contentItemId);
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      toast.success('Linked to content item');
      setUsedInTarget(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to link');
      throw err;
    }
  };

  const visible = items.filter(i => showArchived || !i.archived);
  const archivedCount = items.filter(i => i.archived).length;
  const usedCount = items.filter(i => (i.usedInContentIds || []).length > 0).length;

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Captured', value: items.length },
          { label: 'Used in Content', value: usedCount },
          { label: 'Archived', value: archivedCount },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-xl border p-4"
               style={{ backgroundColor: SYSTEM_COLORS.primaryTint, borderColor: SYSTEM_COLORS.borderSoft }}>
            <p className="text-xs mb-1" style={{ color: BRAND_COLORS.primary }}>{label}</p>
            <p className="text-xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Region filter pills */}
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={() => setRegion('')}
              className="rounded-full px-3 py-1 text-xs font-medium border transition-colors"
              style={{
                borderColor: !region ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft,
                backgroundColor: !region ? SYSTEM_COLORS.primaryTint : 'transparent',
                color: !region ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
              }}>
              All
            </button>
            {REGIONS.map(r => (
              <button key={r} onClick={() => setRegion(r === region ? '' : r)}
                className="rounded-full px-3 py-1 text-xs font-medium border uppercase transition-colors"
                style={{
                  borderColor: region === r ? BRAND_COLORS.primary : SYSTEM_COLORS.borderSoft,
                  backgroundColor: region === r ? SYSTEM_COLORS.primaryTint : 'transparent',
                  color: region === r ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                }}>
                {r}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                 style={{ color: SYSTEM_COLORS.textMuted }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>

        <div className="flex gap-2">
          <button onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => { setEditingItem(null); setShowForm(true); }}
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Plus size={15} /> Capture
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2"
               style={{ borderColor: BRAND_COLORS.primary }} />
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 rounded-xl border border-dashed"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <Globe size={32} style={{ color: SYSTEM_COLORS.textSoft }} />
          <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
            No inspiration captured yet — click Capture to add the first item.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map(item => (
            <InspirationCard key={item.id} item={item}
              onEdit={(i) => { setEditingItem(i); setShowForm(true); }}
              onDelete={handleDelete}
              onArchive={handleArchive}
              onMarkUsed={(i) => setUsedInTarget(i)} />
          ))}
        </div>
      )}

      {/* Load more */}
      {!loading && visible.length >= limit && (
        <div className="flex justify-center pt-2">
          <button onClick={() => setLimit(l => l + 100)}
            className="rounded-lg border px-5 py-2 text-sm"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}>
            Load more
          </button>
        </div>
      )}

      <ItemFormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingItem(null); }}
        onSave={handleSave}
        initial={editingItem}
      />

      <UsedInModal
        isOpen={!!usedInTarget}
        onClose={() => setUsedInTarget(null)}
        onSave={handleMarkUsed}
        itemTitle={usedInTarget?.title || ''}
      />
    </div>
  );
};

export default TrendingFeed;
